import React from 'react';

import { Remove as RemoveIcon } from '@mui/icons-material';
import { Box, Divider, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import { isNotNil } from 'es-toolkit';
import { DateTime, Interval } from 'luxon';

import { DateFormatPatterns } from '@clearverify/constants';
import { Nullable } from '@clearverify/types';
import { formatMoney } from '@clearverify/utils';

import { DashboardPlatformAdminRevenueDistributionItemInfoFragment } from 'src/api';
import { ChartBase, ChartOptions } from 'src/components/chart';
import { ButtonDatePicker } from 'src/components/common';
import { DashboardPalette } from 'src/constants/dashboard';
import { useDashboardCVAdminData, useDashboardPrintView } from 'src/hooks/dashboard';
import { snapToUtcMonthEnd, snapToUtcMonthStart } from 'src/utils/dashboard';

import { DashboardChartWrapper } from '../DashboardChartWrapper';

import { DashboardRevenueShareDimensions, VerificationRequestGroup } from './types';
import { prepareDataset } from './utils';

export type DashboardClearVerifyRevenueShareProps = {
  revenueDistributions: DashboardPlatformAdminRevenueDistributionItemInfoFragment[];
  minDate?: DateTime;
  maxDate?: DateTime;
  loading?: boolean;
};

export const DashboardClearVerifyRevenueShare: React.FC<DashboardClearVerifyRevenueShareProps> = ({
  revenueDistributions,
  minDate = MAY_2023,
  maxDate = NOW,
  loading,
}) => {
  const { isPrintView } = useDashboardPrintView();
  const {
    revenueDistributionsStartDate: startDate,
    revenueDistributionsEndDate: endDate,

    setRevenueDistributionsStartDate: onStartDateChange,
    setRevenueDistributionsEndDate: onEndDateChange,
  } = useDashboardCVAdminData();

  const handleStartDateChange = React.useCallback(
    (date: Nullable<DateTime>) => onStartDateChange(snapToUtcMonthStart(date)),
    [onStartDateChange],
  );

  const handleEndDateChange = React.useCallback(
    (date: Nullable<DateTime>) => onEndDateChange(snapToUtcMonthEnd(date)),
    [onEndDateChange],
  );

  const interval = startDate && endDate ? Interval.fromDateTimes(startDate, endDate) : undefined;

  const months = interval
    ?.splitBy({ months: 1 })
    .map(interval => interval.start?.toFormat(DateFormatPatterns.fullMonthYear))
    .filter(isNotNil);

  const dataset = React.useMemo(
    () => prepareDataset(revenueDistributions),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [revenueDistributions, startDate, endDate],
  );

  const isDatasetEmpty = !dataset.length && !loading;

  const options = React.useMemo(
    (): ChartOptions => ({
      grid: { top: 10, right: 40, bottom: 55, left: 40, containLabel: true },
      animationDuration: isPrintView ? 0 : undefined,
      xAxis: {
        type: 'category',
        data: months,
        axisLabel: {
          fontSize: 14,
          fontWeight: 'bold',
        },
      },
      yAxis: {
        type: 'value',
        position: 'right',
        axisLabel: {
          fontSize: 14,
          fontWeight: 'bold',
          width: '100px',
          formatter: value => `${formatMoney(value.valueOf())}`,
        },
      },
      legend: {
        show: true,
        bottom: 0,
        textStyle: {
          fontSize: 13,
        },
      },
      tooltip: {
        trigger: 'axis',
        valueFormatter: value => formatMoney(Number(value?.valueOf())),
      },
      series: [
        {
          name: VerificationRequestGroup.employers,
          datasetId: VerificationRequestGroup.employers,
          type: 'bar',
          encode: {
            x: DashboardRevenueShareDimensions.month,
            y: DashboardRevenueShareDimensions.revenueSharePayedAmount,
          },
          label: isPrintView ? LABEL_OPTIONS : {},
          itemStyle: {
            borderRadius: 5,
          },
          color: loading ? DashboardPalette.slate50 : DashboardPalette.grey,
        },
        {
          name: VerificationRequestGroup.peo,
          datasetId: VerificationRequestGroup.peo,
          type: 'bar',
          encode: {
            x: DashboardRevenueShareDimensions.month,
            y: DashboardRevenueShareDimensions.revenueSharePayedAmount,
          },
          label: isPrintView ? LABEL_OPTIONS : {},
          itemStyle: {
            borderRadius: 5,
          },
          color: loading ? DashboardPalette.slate50 : DashboardPalette.green400,
        },
        {
          name: VerificationRequestGroup.peoPayrollProvider,
          datasetId: VerificationRequestGroup.peoPayrollProvider,
          type: 'bar',
          encode: {
            x: DashboardRevenueShareDimensions.month,
            y: DashboardRevenueShareDimensions.revenueSharePayedAmount,
          },
          label: isPrintView ? LABEL_OPTIONS : {},
          itemStyle: {
            borderRadius: 5,
          },
          color: loading ? DashboardPalette.slate50 : DashboardPalette.blue200,
        },
      ],
      dataset: [
        {
          id: 'dataset_raw',
          dimensions: [
            DashboardRevenueShareDimensions.month,
            DashboardRevenueShareDimensions.revenueSharePayedAmount,
            DashboardRevenueShareDimensions.type,
          ],
          source: dataset,
        },
        {
          id: VerificationRequestGroup.employers,
          fromDatasetId: 'dataset_raw',
          transform: [
            {
              type: 'filter',
              config: {
                dimension: DashboardRevenueShareDimensions.type,
                '=': VerificationRequestGroup.employers,
              },
            },
          ],
        },
        {
          id: VerificationRequestGroup.peo,
          fromDatasetId: 'dataset_raw',
          transform: [
            {
              type: 'filter',
              config: {
                dimension: DashboardRevenueShareDimensions.type,
                '=': VerificationRequestGroup.peo,
              },
            },
          ],
        },
        {
          id: VerificationRequestGroup.peoPayrollProvider,
          fromDatasetId: 'dataset_raw',
          transform: [
            {
              type: 'filter',
              config: {
                dimension: DashboardRevenueShareDimensions.type,
                '=': VerificationRequestGroup.peoPayrollProvider,
              },
            },
          ],
        },
      ],
    }),
    [dataset, isPrintView, loading, months],
  );

  return (
    <Paper sx={{ py: 2.5, px: 3, borderRadius: 2, overflow: 'hidden' }} variant="outlined">
      <Stack spacing={2}>
        <Box>
          <Typography variant="subtitle1">Clear Verify Revenue Share Distributions</Typography>
          <Typography whiteSpace="pre-line" variant="body1" color="text.secondary">
            Breakdown revenue share by account type on a monthly basis for the provided date range.
          </Typography>
        </Box>
        <Box height="4px">{loading ? <LinearProgress color="secondary" /> : <Divider />}</Box>

        <Stack direction="row" alignItems="center" spacing={1}>
          <ButtonDatePicker
            views={['month', 'year']}
            timezone="UTC"
            minDate={minDate}
            maxDate={endDate || maxDate}
            value={startDate}
            onChange={handleStartDateChange}
            disabled={loading}
          />
          <Box display="flex" alignItems="center" fontSize="13px">
            <RemoveIcon fontSize="inherit" />
          </Box>
          <ButtonDatePicker
            views={['month', 'year']}
            timezone="UTC"
            minDate={minDate}
            maxDate={maxDate}
            value={endDate}
            onChange={handleEndDateChange}
            disabled={loading}
          />
        </Stack>

        {Boolean(months?.length) && (
          <DashboardChartWrapper
            isDatasetEmpty={isDatasetEmpty}
            wrapperHeight={300}
            isPrintView={isPrintView}
          >
            <ChartBase option={options} style={{ height: 300 }} />
          </DashboardChartWrapper>
        )}
      </Stack>
    </Paper>
  );
};

const NOW = DateTime.utc();
const MAY_2023 = DateTime.fromISO('2023-05-01T00:00:00.000', { zone: 'utc' }).startOf('month');

const LABEL_OPTIONS = {
  show: true,
  fontSize: '16px',
  fontWeight: 'bold' as const,
  formatter: (params: any) => formatMoney(params.data.revenueSharePayedAmount),
  position: 'inside' as const,
  offset: [60, 0],
  rotate: 90,
  inside: false,
};
